import type { AuthUser, MeResponse } from '@alluzo/shared';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

import { useSessionStatus } from '@/hooks/use-session-status';
import { fetchMe } from '@/lib/api';
import { authClient, getCookie } from '@/lib/auth';
import { cachedMe, cacheMe, forgetDevice } from '@/lib/entry-repo';
import { readDeviceLocale } from '@/lib/device-locale';
import { describeApiFailure } from '@/lib/failure-messages';
import { dueOnActive, meChanged } from '@/lib/me-refresh';
import { mayTrustCachedIdentity } from '@/lib/sync-failure';
import { usePerspectiveStore } from '@/stores/perspective-store';
import { useSetupProgressStore } from '@/stores/setup-progress-store';
import { useSyncStore } from '@/stores/sync-store';

export type MeState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; me: MeResponse }
  | { status: 'failed'; message: string };

export type MeContextValue = {
  state: MeState;
  reload: () => void;
  /**
   * A write this phone has just made, kept in the held answer. A refetch would
   * go through `loading` and blank every screen reading this.
   */
  patchUser: (patch: Partial<AuthUser>) => void;
  /**
   * `/v1/me` read again quietly: the held answer stays on screen until the new
   * one lands, and a failure keeps it (step 6.3 of docs/plans/roles-parity.md).
   * `write` reads now, after a write of this phone's own that can move the
   * caller's standing; `active` reads on coming back to the app, at most once a
   * minute (`dueOnActive`).
   */
  refresh: (why: 'write' | 'active') => void;
};

const MeContext = createContext<MeContextValue | null>(null);

/**
 * GET /v1/me, once the session is signed in, shared by the gate and the home
 * screen. After onboarding apply, `reload` is what flips the gate from the
 * funnel to home: the next `/v1/me` carries the new membership.
 *
 * The answer is remembered on the device, and read back when the request
 * cannot reach the server. Without that fallback this hook decided whether the
 * whole app worked: the mirror could hold a year of entries, but every screen
 * keys off the tenant id from here, so one unreachable request blanked a
 * device that already had everything it needed. An offline-first app cannot
 * have a network call on its critical path.
 *
 * The fallback covers everything except a 401 or a 403. Those two are the
 * server's answer about this person and must be obeyed, because that is how
 * signing out elsewhere or losing a membership reaches this device. A missing
 * route or a 500 says nothing about who they are and must not sign them out.
 */
export function MeProvider({ children }: { children: ReactNode }): ReactElement {
  const session = useSessionStatus();
  const [state, setState] = useState<MeState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);
  /** When `/v1/me` last answered, for the once-a-minute re-read on coming back. */
  const lastReadAt = useRef<number | null>(null);
  /** The answer held now and whether the session is signed in, read by `refresh`. */
  const held = useRef<{ state: MeState; signedIn: boolean }>({ state, signedIn: session === 'signed-in' });
  held.current = { state, signedIn: session === 'signed-in' };
  /** One quiet re-read at a time; a second asked meanwhile reads once more after it. */
  const reading = useRef<{ again: boolean } | null>(null);

  useEffect(() => {
    if (session !== 'signed-in') {
      // Only an explicit sign-out wipes the device. `loading` is a few frames
      // on every boot, and wiping there would defeat the whole point of having
      // a cache. The store goes with the tables: both hold the same state.
      if (session === 'signed-out') {
        forgetDevice();
        useSyncStore.getState().reset();
        // The get-going card's skips went with the rows; the copy in memory
        // must not outlive them and dress the next account's Dashboard.
        useSetupProgressStore.getState().reset();
        // Whose eyes the app was drawn through goes with the session. The next
        // person to sign in on this phone is themselves.
        usePerspectiveStore.getState().exit();
      }
      setState({ status: 'idle' });
      return;
    }

    let abandoned = false;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const me = await fetchMe(await getCookie());
        if (!abandoned) {
          lastReadAt.current = Date.now();
          cacheMe(me);
          setState({ status: 'ready', me });
        }
      } catch (thrown) {
        if (abandoned) return;
        // Fall back to what this device already knows, so the mirror stays
        // readable. Only a 401 or a 403 is really about this person; a missing
        // route or a 500 is about the server, and must not sign anybody out.
        const held = mayTrustCachedIdentity(thrown) ? cachedMe<MeResponse>() : null;
        if (held !== null) {
          setState({ status: 'ready', me: held });
          return;
        }
        setState({ status: 'failed', message: describeApiFailure(thrown) });
      }
    })();

    return () => {
      abandoned = true;
    };
  }, [session, attempt]);

  // A person who signed up before these columns, or whose sign-up did not
  // carry them, gets one write from this device. Talk still sends a live
  // read, so the agent does not wait on this.
  useEffect(() => {
    if (state.status !== 'ready') return;
    if (state.me.user.timezone != null && state.me.user.timezone !== '') return;
    const device = readDeviceLocale();
    if (device.timeZone === null) return;
    void authClient.updateUser({
      timezone: device.timeZone,
      ...(device.locale === null ? {} : { locale: device.locale }),
      ...(device.region === null ? {} : { region: device.region }),
      ...(device.currency === null ? {} : { localeCurrency: device.currency }),
    });
  }, [state]);

  const reload = (): void => {
    setAttempt((count) => count + 1);
  };

  const refresh = useCallback((why: 'write' | 'active'): void => {
    const now = held.current;
    // Only over an answer already on screen: a first read, a sign-in or a
    // failure is `reload`'s, which shows its loading.
    if (!now.signedIn || now.state.status !== 'ready') return;
    if (why === 'active' && !dueOnActive(lastReadAt.current, Date.now())) return;
    if (reading.current !== null) {
      reading.current.again = true;
      return;
    }
    const run = { again: false };
    reading.current = run;
    void (async () => {
      try {
        const me = await fetchMe(await getCookie());
        lastReadAt.current = Date.now();
        const after = held.current;
        // Signed out, or a full read began, while this one was out: theirs stands.
        if (!after.signedIn || after.state.status !== 'ready') return;
        if (!meChanged(after.state.me, me)) return;
        cacheMe(me);
        setState({ status: 'ready', me });
      } catch {
        // The held answer stands. A 401 or a 403 reaches this phone through
        // the next full read and the sync, which obey it; a quiet re-read
        // never signs anybody out.
      } finally {
        reading.current = null;
        if (run.again) refresh('write');
      }
    })();
  }, []);

  const patchUser = (patch: Partial<AuthUser>): void => {
    if (state.status !== 'ready') return;
    const me = { ...state.me, user: { ...state.me.user, ...patch } };
    cacheMe(me);
    setState({ status: 'ready', me });
  };

  return <MeContext.Provider value={{ state, reload, patchUser, refresh }}>{children}</MeContext.Provider>;
}

export const useMe = (): MeContextValue => {
  const value = useContext(MeContext);
  if (value === null) {
    throw new Error('useMe must be used inside MeProvider.');
  }
  return value;
};
